import React, { useCallback, useEffect, useMemo, useRef, useState, } from "react";
import { formatCurrency, suspendResource } from "../../utils";
import { useTranslation } from "react-i18next";
import { useAuth } from "./AuthProvider";
import { useWebshopApiClient } from "@/react/Webshop/providers/WebshopApiClientProvider";
export const PriceListContext = React.createContext(null);
export const PRODUCT_CATALOGUE_UUID = "b4fd44ad-6e87-4be1-bf9a-05ad224d7c50";
export const PRODUCT_SALES_TAX_UUID = "ad2262da-f396-46a9-87d8-edb12de85b95";
export const PRODUCT_RETOUCH_FILE_UUID = "2bfcdbeb-9293-23a0-00d8-36c50878f44c";
export const PRODUCT_DIGITAL_SET_UUID = "9c99830e-b4ae-4afb-b1cf-b6f445c1806d";
export const PRODUCT_DIGITAL_FILE_UUID = "06e3c44b-7c63-4020-b4ac-42b5333b8b4a";
export const PRODUCT_DIGITAL_GROUP_FILE_UUID = "cdaee0a7-1544-4fbd-a285-ade9a492e4b8";
export const PRODUCT_DIGITAL_GROUP_SET_UUID = "3d63dec2-6650-4cc9-b497-93f6ee82ebdc";
export const PRODUCT_DIGITAL_MINI_GROUP_FILE_UUID = "1b5f4e5b-d826-406a-90a9-d55c34604477";
export const PRODUCT_DIGITAL_MINI_GROUP_SET_UUID = "b923e220-03e6-4778-81ee-09ed909dff86";
export const PRODUCT_DIGITAL_PORTRAIT_IP_SIBLING_AND_FRIEND_FILE_UUID = "22bed49a-720f-44cf-a166-db21af0801a4";
export const PRODUCT_DIGITAL_PORTRAIT_IP_SIBLING_AND_FRIEND_SET_UUID = "a2641290-cf43-4320-9592-96c4481c0708";
export const PRODUCT_PACKAGE_UUID = "01f1b7eb-2a82-4b46-9078-551bf9031d0c";
export const REBATE_CODE_UUID = "332b84be-5ce6-4156-b17c-f8bdc1c4a3c9";
export const PRODUCT_INVOICE_UUID = "a0957c46-ed22-426a-8263-28e47541dfad";
export const PRODUCT_EMAIL_INVOICE_UUID = "5517da09-96ef-418e-b341-09409961d641";
export const PRODUCT_INVOICE_LONG_TERM_UUID = "b048a8b8-96fd-4ff1-9896-d78d656ababf";
export const productUuidIsInvoiceFeeProduct = (uuid) => {
    return ([
        PRODUCT_INVOICE_UUID,
        PRODUCT_EMAIL_INVOICE_UUID,
        PRODUCT_INVOICE_LONG_TERM_UUID,
    ].indexOf(uuid) >= 0);
};
export const productUuidIsDigitalFileProduct = (uuid) => {
    return ([
        PRODUCT_DIGITAL_FILE_UUID,
        PRODUCT_DIGITAL_PORTRAIT_IP_SIBLING_AND_FRIEND_FILE_UUID,
        PRODUCT_DIGITAL_MINI_GROUP_FILE_UUID,
        PRODUCT_DIGITAL_GROUP_FILE_UUID,
    ].indexOf(uuid) >= 0);
};
export const productUuidIsDigitalSetProduct = (uuid) => {
    return ([
        PRODUCT_DIGITAL_SET_UUID,
        PRODUCT_DIGITAL_PORTRAIT_IP_SIBLING_AND_FRIEND_SET_UUID,
        PRODUCT_DIGITAL_MINI_GROUP_SET_UUID,
        PRODUCT_DIGITAL_GROUP_SET_UUID,
    ].indexOf(uuid) >= 0);
};
// We should export all functions like this?
export const isWhiteBorderProduct = (product) => {
    return !!(product.producttype == 0 &&
        product.name.match(/(white border|hvit kant|vita kanter|valkoiset reunat|Hvítur kantur)/i));
};
const fixPriceListPriceListPrices = (priceList) => {
    for (const product of priceList.products) {
        for (const productPrice of product.productprices) {
            if (typeof productPrice.price === "string") {
                productPrice.price = parseFloat(productPrice.price);
            }
            if (productPrice.campaignprice &&
                typeof productPrice.campaignprice === "string") {
                productPrice.campaignprice = parseFloat(productPrice.campaignprice);
            }
        }
    }
    return priceList;
};
let initialPriceListResource = null;
function getPriceListSuspendResource(shoppingCartClient) {
    if (!initialPriceListResource) {
        const initialPriceList = shoppingCartClient.getInitialPricelist();
        if (initialPriceList) {
            initialPriceListResource = suspendResource(Promise.resolve(initialPriceList));
        }
        else {
            initialPriceListResource = suspendResource(shoppingCartClient.getPriceList());
        }
    }
    return initialPriceListResource;
}
const PriceListProvider = ({ children }) => {
    const apiClient = useWebshopApiClient();
    const initialCartSuspendResource = getPriceListSuspendResource(apiClient);
    const initialData = useRef(initialCartSuspendResource.read());
    const [state, setState] = useState(() => {
        return fixPriceListPriceListPrices(initialData.current);
    });
    const { user } = useAuth();
    const { t } = useTranslation();
    const userRef = useRef(user);
    useEffect(() => {
        // No change:
        if (userRef.current == user) {
            return;
        }
        updatePriceList();
    }, [user]);
    const updatePriceList = useCallback(() => {
        apiClient
            .getPriceList()
            .then((r) => setState(fixPriceListPriceListPrices(r)));
    }, [apiClient]);
    const getProductByUuid = (uuid) => {
        console.log("Get product by uuid", uuid, state.products);
        return state.products.find((product) => product.uuid == uuid);
    };
    const getPrintFormats = () => {
        console.log("Products", state.products);
        const printFormats = state.products
            .filter((product) => {
            console.log(product.producttype, product.name);
            return product.producttype == 0;
            // Elitefoto used to not include white border:
            //return product.producttype == 0 && !product.name.match(/(hvit kant)/i);
        })
            .sort((a, b) => {
            return a.name.localeCompare(b.name, undefined, {
                numeric: true,
                sensitivity: "base",
            });
        });
        console.log("Print formats", printFormats);
        return printFormats;
    };
    const getFormatSizeName = (product) => {
        const re = product.name.match(/^(\d+x\d+)\b/i);
        if (re) {
            return re[1];
        }
        return product.name;
    };
    const isSameFormat = (product1, product2) => {
        const name = getFormatSizeName(product1);
        const reg = new RegExp("^" + name + "\\b");
        return !!product2.name.match(reg);
    };
    const getWhiteBorderAlternativeProduct = (product) => {
        const whiteBorderProduct = state.products.find((p) => {
            return isWhiteBorderProduct(p) && isSameFormat(p, product);
        });
        console.log("White border product", whiteBorderProduct);
        return whiteBorderProduct;
    };
    const getNonWhiteBorderAlternativeProduct = (product) => {
        const whiteBorderProduct = state.products.find((p) => {
            return !isWhiteBorderProduct(p) && isSameFormat(p, product);
        });
        console.log("Non white border product", whiteBorderProduct);
        return whiteBorderProduct;
    };
    const getSmallestPaperSize = () => {
        return state.products.reduce((smallestSize, product) => {
            if (product.producttype != 0) {
                return smallestSize;
            }
            const match = product.name.match(/(\d+)x(\d+)/);
            if (match) {
                const width = parseInt(match[1]);
                const height = parseInt(match[2]);
                console.log("Found match in format", width, height);
                if (width * height < smallestSize.width * smallestSize.height) {
                    return {
                        width,
                        height,
                    };
                }
                else {
                    console.log("Existing format is smaller", smallestSize);
                }
                return smallestSize;
            }
            return smallestSize;
        }, { width: Number.MAX_SAFE_INTEGER, height: Number.MAX_SAFE_INTEGER });
    };
    const getLargestPaperSize = () => {
        return state.products.reduce((largestSize, product) => {
            if (product.producttype != 0) {
                return largestSize;
            }
            const match = product.name.match(/(\d+)x(\d+)/);
            if (match) {
                const width = parseInt(match[1]);
                const height = parseInt(match[2]);
                if (width * height > largestSize.width * largestSize.height) {
                    return {
                        width,
                        height,
                    };
                }
                return largestSize;
            }
            return largestSize;
        }, { width: 0, height: 0 });
    };
    const productHasCampaign = (product, category) => {
        if (product.campaignrequirement &&
            category != product.campaignrequirement) {
            return false;
        }
        return (product.productprices.filter((pp) => pp.campaignprice !== null).length >
            0);
    };
    const getPromotedPrice = (product) => {
        var _a;
        console.log("Get promoted price", product, state.promotedProducts);
        const promotedProduct = (_a = state.promotedProducts) === null || _a === void 0 ? void 0 : _a.find((p) => p.product.uuid == product.uuid);
        if (promotedProduct) {
            return promotedProduct.rebatedPrice;
        }
        return null;
    };
    const getPriceAsString = (product, categories) => {
        const pp = product.productprices;
        let hasCampaign = false;
        if (categories) {
            for (const category of categories) {
                if (productHasCampaign(product, category)) {
                    hasCampaign = true;
                    break;
                }
            }
        }
        if (hasCampaign) {
            if (pp.length == 1) {
                return (React.createElement(React.Fragment, null,
                    React.createElement("span", { className: "original-price" }, formatCurrency(pp[0].price)),
                    " ",
                    formatCurrency(pp[0].campaignprice)));
            }
            return t("Campaign prices from %s", {
                postProcess: "sprintf",
                sprintf: [formatCurrency(pp[pp.length - 1].campaignprice)],
            });
        }
        else {
            if (pp.length == 1) {
                return formatCurrency(pp[0].price);
            }
            return t("Prices from %s", {
                postProcess: "sprintf",
                sprintf: [formatCurrency(pp[pp.length - 1].price)],
            });
        }
    };
    const getPricePerUnit = (product, quantity, hasCampaignPrice, qualifyForPromotedPrice) => {
        if (qualifyForPromotedPrice) {
            const promotedPrice = getPromotedPrice(product);
            if (promotedPrice !== null) {
                return promotedPrice;
            }
        }
        let ppu = -1.0;
        for (const productPrice of product.productprices) {
            if (quantity < productPrice.quantity) {
                console.log("Exit price per unit at", productPrice.quantity, ppu);
                break;
            }
            if (quantity >= productPrice.quantity) {
                if (hasCampaignPrice && productPrice.campaignprice !== null) {
                    ppu = productPrice.campaignprice;
                }
                else {
                    ppu = productPrice.price;
                }
                console.log("Per unit is larger", productPrice, quantity, ppu);
            }
        }
        console.log("Return per unit at", ppu);
        return ppu;
    };
    const calculateLinePrice = (productUuid, quantity, subProductUuid, hasCampaignPrice, qualifyForPromotedPrice) => {
        var _a;
        const product = getProductByUuid(productUuid);
        if (!product) {
            return -1;
        }
        let priceScheme = 1;
        if (product.productprices[0].type == 0) {
            priceScheme = 0;
        }
        console.log("Qualify for promoted prices", qualifyForPromotedPrice);
        let ppu = getPricePerUnit(product, quantity, hasCampaignPrice, qualifyForPromotedPrice);
        let subProductPrice = 0;
        if (subProductUuid) {
            const subProduct = (_a = product.subproducts) === null || _a === void 0 ? void 0 : _a.find((sp) => sp.uuid == subProductUuid);
            if (subProduct) {
                subProductPrice = subProduct.extraprice;
            }
        }
        console.log("Price per unit", ppu, typeof ppu, product);
        // FIXME: Sub products and campaign prices?
        if (priceScheme == 0) {
            return ppu * quantity + subProductPrice * quantity;
        }
        console.log("Using unit pricing", product.productprices);
        // Unit pricing:
        let totalPrice = 0;
        for (let i = 0; i < quantity; i++) {
            ppu = getPricePerUnit(product, i + 1, hasCampaignPrice);
            totalPrice += ppu + subProductPrice;
        }
        return totalPrice;
    };
    const hasRetouchProduct = useMemo(() => {
        return !!getProductByUuid(PRODUCT_RETOUCH_FILE_UUID);
    }, [state]);
    return (React.createElement(PriceListContext.Provider, { value: {
            apiClient,
            state,
            getProductByUuid,
            getPrintFormats,
            calculateLinePrice,
            getWhiteBorderAlternativeProduct,
            isSameFormat,
            isWhiteBorderProduct,
            hasRetouchProduct,
            getNonWhiteBorderAlternativeProduct,
            getFormatSizeName,
            getSmallestPaperSize,
            getLargestPaperSize,
            getPriceAsString,
            getPricePerUnit,
            productHasCampaign,
            getPromotedPrice,
            updatePriceList,
        } }, children));
};
const usePriceList = () => {
    const context = React.useContext(PriceListContext);
    if (!context) {
        throw new Error("usePriceList must be used within a <PriceListProvider>");
    }
    return context;
};
export { usePriceList };
export default PriceListProvider;
